import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useEffect, useRef } from 'react';
import { 
  Table, 
  Button, 
  Tag, 
  Form, 
  DatePicker, 
  Input, 
  Row, 
  Col, 
  Typography,
  Spin,
  Tabs,
  Badge,
  Collapse,
  message,
  Space,
  Popconfirm,
  Modal,
  List,
  Avatar
} from 'antd';
import { 
  ShoppingOutlined, 
  InfoCircleOutlined,
  TruckOutlined,
  FilterOutlined,
  SearchOutlined,
  ClearOutlined,
  DownOutlined,
  FileTextOutlined,
  SaveOutlined,
  SyncOutlined,
  PrinterOutlined,
  FileSearchOutlined,
  ArrowLeftOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';

import { 
  useFetchJournalWithoutExpRef, 
  useFetchJournalWithExpRef, 
  useUpdateJournalEntry 
} from '../../hooks/expedHooks';

import { EditableRow, EditableCell, KeyboardNavContext } from './EditableComponents';
import printTemplate from '../../utils/printExpTemplate';
import previewTemplate from '../../utils/printExpPreview';


// Import template hooks
import { useTemplates } from '../../hooks/templateHooks';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;
const { Title } = Typography;

// Template selection modal component
const TemplateSelectionModal = ({ visible, onCancel, rowData }) => {
  const { templates, loading, error } = useTemplates();
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Handle template selection for printing
  const handlePrint = async (template) => {
    if (!template || !template.data || !rowData) {
      message.error("Données incorrectes pour l'impression.");
      return;
    }

    try {
      // Import the print function dynamically to avoid circular dependencies
      const printTemplate = (await import('../../utils/printExpTemplate')).default;
      
      // Print the template with the row data
      printTemplate(template.data, rowData);
      
      // Close the modal
      onCancel();
      message.success(`Impression avec le modèle "${template.name}" lancée.`);
    } catch (error) {
      console.error("Erreur d'impression:", error);
      message.error("Erreur lors de l'impression du document.");
    }
  };

  // Handle template preview
  const handlePreview = async (template) => {
    if (!template || !template.data || !rowData) {
      message.error("Données incorrectes pour l'aperçu.");
      return;
    }

    try {
      setPreviewLoading(true);
      
      // Import the preview function dynamically to avoid circular dependencies
      const previewTemplate = (await import('../../utils/printExpPreview')).default;
      
      // Generate preview URL
      const url = await previewTemplate(template.data, rowData);
      setPreviewUrl(url);
      setPreviewMode(true);
      
      message.success(`Aperçu du modèle "${template.name}" généré.`);
    } catch (error) {
      console.error("Erreur d'aperçu:", error);
      message.error("Erreur lors de la génération de l'aperçu.");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle back button in preview mode
  const handleBackToTemplates = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewMode(false);
  };

  // Handle close of modal
  const handleClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewMode(false);
    onCancel();
  };

  // Loading state
  if (loading) {
    return (
      <Modal
        title="Sélectionner un modèle d'impression"
        open={visible}
        onCancel={handleClose}
        footer={null}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '10px' }}>Chargement des modèles...</p>
        </div>
      </Modal>
    );
  }

  // Error state
  if (error) {
    return (
      <Modal
        title="Erreur"
        open={visible}
        onCancel={handleClose}
        footer={[
          <Button key="close" onClick={handleClose}>
            Fermer
          </Button>
        ]}
      >
        <div style={{ color: '#ff4d4f', textAlign: 'center' }}>
          <InfoCircleOutlined style={{ fontSize: '24px', marginBottom: '10px' }} />
          <p>Erreur lors du chargement des modèles: {error}</p>
        </div>
      </Modal>
    );
  }

  // Preview mode
  if (previewMode) {
    return (
      <Modal
        title="Aperçu du document"
        open={visible}
        onCancel={handleClose}
        width="90%"
        style={{ top: 20 }}
        footer={[
          <Button 
            key="back" 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBackToTemplates}
          >
            Retour
          </Button>,
          <PermissionButton permissionModule="expedition" permissionAction="print"
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />}
            onClick={() => {
              // Get the currently selected template
              const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
              if (selectedTemplate) {
                handlePrint(selectedTemplate);
              }
            }}
          >
            Imprimer
          </PermissionButton>
        ]}
      >
        {previewLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <p style={{ marginTop: '16px' }}>Génération de l'aperçu...</p>
          </div>
        ) : previewUrl ? (
          <iframe
            src={previewUrl}
            style={{ width: '100%', height: '80vh', border: 'none' }}
            title="Print Preview"
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>Erreur: Impossible de générer l'aperçu</p>
          </div>
        )}
      </Modal>
    );
  }

  // Template selection mode (default)
  return (
    <Modal
      title="Sélectionner un modèle d'impression"
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Annuler
        </Button>
      ]}
      width={600}
    >
      {templates && templates.length > 0 ? (
        <List
          itemLayout="horizontal"
          dataSource={templates}
          renderItem={template => (
            <List.Item
              key={template.id}
              actions={[
                <Space>
                  <Button 
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTemplateId(template.id);
                      handlePreview(template);
                    }}
                  >
                    Aperçu
                  </Button>
                  <PermissionButton permissionModule="expedition" permissionAction="print"
                    type="primary" 
                    icon={<PrinterOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrint(template);
                    }}
                  >
                    Imprimer
                  </PermissionButton>
                </Space>
              ]}
              style={{
                padding: '12px',
                border: '1px solid #f0f0f0',
                borderRadius: '4px',
                marginBottom: '8px',
                cursor: 'pointer',
                backgroundColor: selectedTemplateId === template.id ? '#e6f7ff' : 'white'
              }}
              onClick={() => setSelectedTemplateId(template.id)}
            >
              <List.Item.Meta
                avatar={<Avatar icon={<FileTextOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                title={template.name}
                description={template.description || 'Aucune description disponible'}
              />
            </List.Item>
          )}
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>Aucun modèle disponible. Veuillez d'abord créer un modèle dans la section Modèles.</p>
          <Button type="primary" style={{ marginTop: '10px' }}>
            Créer un modèle
          </Button>
        </div>
      )}
    </Modal>
  );
};


const JournalExpTable = ({ compte, clientName }) => {
  // Base spacing system
  const sizeUnit = 2; // Base unit in pixels
  const sizeStep = {
    xxs: sizeUnit / 2, // 1px
    xs: sizeUnit,      // 2px
    sm: sizeUnit * 2,  // 4px
    md: sizeUnit * 3,  // 6px
    lg: sizeUnit * 4,  // 8px
    xl: sizeUnit * 6,  // 12px
    xxl: sizeUnit * 8, // 16px
  };
  
  // Fetch data for different tabs
  const { 
    journalEntries: entriesWithoutExp, 
    loading: loadingWithoutExp, 
    error: errorWithoutExp, 
    refetch: refetchWithoutExp 
  } = useFetchJournalWithoutExpRef(compte);
  
  const { 
    journalEntries: entriesWithExp, 
    loading: loadingWithExp, 
    error: errorWithExp, 
    refetch: refetchWithExp 
  } = useFetchJournalWithExpRef(compte);
  
  const { updateJournalEntry, loading: updateLoading } = useUpdateJournalEntry();
  
  // State
  const [activeTab, setActiveTab] = useState('1');
  const [filterForm] = Form.useForm();
  const [savingChanges, setSavingChanges] = useState(false);
  
  // For editable cells and keyboard navigation
  const [pendingChanges, setPendingChanges] = useState(new Map());
  const [originalData, setOriginalData] = useState(new Map());
  const [activeCell, setActiveCell] = useState(null);
  const [activeRowKey, setActiveRowKey] = useState(null);
  const cellRefs = useRef({});

  // Filter states
  const [filters, setFilters] = useState({
    ref_dev: '',
    ref_fac: '',
    ref_exp: '',
    date_exp: null
  });

  // Template modal states
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  // Filtered data states
  const [filteredWithoutExp, setFilteredWithoutExp] = useState([]);
  const [filteredWithExp, setFilteredWithExp] = useState([]);

  // Track original data for each entry to detect modifications
  useEffect(() => {
    const newOriginalData = new Map();
    
    const processEntries = (entries) => {
      if (Array.isArray(entries)) {
        entries.forEach(entry => {
          if (entry && entry.id) {
            newOriginalData.set(entry.id, { ...entry });
          }
        });
      }
    };
    
    processEntries(entriesWithoutExp);
    processEntries(entriesWithExp);
    
    setOriginalData(newOriginalData);
  }, [entriesWithoutExp, entriesWithExp]);

  // Apply filters to data
  useEffect(() => {
    if (entriesWithoutExp) {
      setFilteredWithoutExp(filterEntries(entriesWithoutExp));
    }
    if (entriesWithExp) {
      setFilteredWithExp(filterEntries(entriesWithExp));
    }
  }, [filters, entriesWithoutExp, entriesWithExp]);

  // Filter function
  const filterEntries = (entries) => {
    if (!entries) return [];
    
    return entries.filter(entry => {
      // Check ref_dev filter
      if (filters.ref_dev && (!entry.ref_dev || !entry.ref_dev.toLowerCase().includes(filters.ref_dev.toLowerCase()))) {
        return false;
      }
      
      // Check ref_fac filter
      if (filters.ref_fac && (!entry.ref_fac || !entry.ref_fac.toLowerCase().includes(filters.ref_fac.toLowerCase()))) {
        return false;
      }
      
      // Check ref_exp filter
      if (filters.ref_exp && (!entry.ref_exp || !entry.ref_exp.toLowerCase().includes(filters.ref_exp.toLowerCase()))) {
        return false;
      }
      
      // Check date_exp filter
      if (filters.date_exp && entry.date_exp) {
        const [startDate, endDate] = filters.date_exp;
        const entryDate = new Date(entry.date_exp);
        
        if (startDate && endDate) {
          return entryDate >= startDate && entryDate <= endDate;
        }
      }
      
      return true;
    });
  };

  // Handle filter change
  const handleFilterChange = (changedValues, allValues) => {
    const newFilters = { ...filters };
    
    Object.keys(changedValues).forEach(key => {
      newFilters[key] = changedValues[key];
    });
    
    setFilters(newFilters);
  };

  // Reset filters
  const resetFilters = () => {
    filterForm.resetFields();
    setFilters({
      ref_dev: '',
      ref_fac: '',
      ref_exp: '',
      date_exp: null
    });
  };

  // Format date or return placeholder
  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return format(new Date(date), 'dd/MM/yyyy');
    } catch (e) {
      return 'Invalid';
    }
  };

  // Format currency amount
  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return amount.toFixed(2);
  };

  // Handle printing a row
  const handlePrintRow = (record) => {
    console.log(record);
    setSelectedRowData(record);
    setTemplateModalVisible(true);
  };

  // Handle template selection for printing
  const handleTemplateSelect = (template, rowData) => {
    if (!template || !template.data || !rowData) {
      message.error("Données incorrectes pour l'impression.");
      return;
    }

    try {
      // Print the template with the row data
      printTemplate(template.data, rowData);
      
      // Close the modal
      setTemplateModalVisible(false);
      message.success(`Impression avec le modèle "${template.name}" lancée.`);
    } catch (error) {
      console.error("Erreur d'impression:", error);
      message.error("Erreur lors de l'impression du document.");
    }
  };

  // Add this function to convert dates properly before sending to Prisma
  const parseDateForPrisma = (dateString) => {
    if (!dateString) return null;
    
    // Check if it's already in YYYY-MM-DD format
    const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [_, year, month, day] = isoMatch;
      // Create a valid date with the time set to noon to avoid timezone issues
      return new Date(`${year}-${month}-${day}T12:00:00.000Z`);
    }
    
    // Check if it's in DD/MM/YYYY format
    const dmyMatch = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dmyMatch) {
      const [_, day, month, year] = dmyMatch;
      // Create a valid date with the time set to noon to avoid timezone issues
      return new Date(`${year}-${month}-${day}T12:00:00.000Z`);
    }
    
    // If it's already a Date object, return it
    if (dateString instanceof Date && !isNaN(dateString)) {
      return dateString;
    }
    
    // Try to parse as is (last resort)
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }
    
    // Return null for invalid dates instead of Invalid Date
    return null;
  };

  // Modify the saveAllChanges function to use this parser
  const saveAllChanges = async () => {
    if (pendingChanges.size === 0) {
      message.info('Aucune modification à enregistrer');
      return;
    }
    
    setSavingChanges(true);
    
    try {
      const changePromises = [];
      
      // Process each entry with changes
      pendingChanges.forEach((changes, recordId) => {
        // Get original record
        const original = originalData.get(recordId);
        if (!original) return;
        
        // Create updated record
        const updatedRecord = { ...original };
        
        // Process each changed field
        Object.entries(changes).forEach(([field, value]) => {
          // Process date fields
          if (field === 'date_exp') {
            updatedRecord[field] = parseDateForPrisma(value);
          } 
          // Process numeric fields
          else if (field === 'nbr_colis' || field === 'mt_remb') {
            updatedRecord[field] = value !== null && value !== '' ? Number(value) : null;
          }
          // All other fields
          else {
            updatedRecord[field] = value;
          }
        });
        
        // Add to promises
        changePromises.push(updateJournalEntry(recordId, updatedRecord));
      });
      
      // Execute all update promises
      const results = await Promise.all(changePromises);
      
      // Check if all were successful
      const allSuccessful = results.every(result => result.success);
      
      if (allSuccessful) {
        message.success(`${pendingChanges.size} entrées mises à jour avec succès`);
        setPendingChanges(new Map()); // Clear all pending changes
        
        // Refresh data
        refetchWithoutExp();
        refetchWithExp();
      } else {
        message.error('Certaines mises à jour ont échoué');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      message.error('Erreur lors de l\'enregistrement des modifications');
    } finally {
      setSavingChanges(false);
    }
  };

  // Handle discarding all changes
  const discardAllChanges = () => {
    if (pendingChanges.size === 0) {
      message.info('Aucune modification à annuler');
      return;
    }
    
    setPendingChanges(new Map());
    message.success('Modifications annulées');
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  // Define table columns
  const columns = [
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'left',
      width: 80,
      render: (_, record) => (
        <PermissionButton permissionModule="expedition" permissionAction="print"
          icon={<PrinterOutlined />}
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handlePrintRow(record);
          }}
          title="Imprimer"
        />
      ),
    },
    {
      title: () => (
        <div style={{ color: '#1890ff', fontWeight: 600 }}>Devis</div>
      ),
      children: [
        {
          title: 'Réf dev',
          dataIndex: 'ref_dev',
          key: 'ref_dev',
          width: 120,
          fixed: 'left',
          render: text => text || '-',
        },
        {
          title: 'Date dv',
          dataIndex: 'date_dev',
          key: 'date_dev',
          width: 100,
          render: text => formatDate(text),
        },
 
        {
          title: 'modeliv_dv',
          dataIndex: 'modeliv_dv',
          key: 'modeliv_dv',
          width: 100,
          
        },
        {
          title: 'modep_dv',
          dataIndex: 'modep_dv',
          key: 'modep_dv',
          width: 100,
         
        },
        {
          title: 'Mnt dv',
          dataIndex: 'mnt_ttc_dv',
          key: 'mnt_ttc_dv',
          width: 100,
          render: text => text ? formatAmount(text) : '-',
        },
        {
          title: 'Art_X',
          dataIndex: 'art_x',
          key: 'art_x',
          width: 70,
          render: text => text || '-',
        }
      ]
    },
    {
      title: () => (
        <div style={{ color: '#722ed1', fontWeight: 600 }}>Facture</div>
      ),
      children: [
        {
          title: 'Réf fac',
          dataIndex: 'ref_fac',
          key: 'ref_fac',
          width: 120,
          render: text => text || '-',
        },
        {
          title: 'Date fac',
          dataIndex: 'date_fac',
          key: 'date_fac',
          width: 100,
          render: text => formatDate(text),
        },
        {
          title: 'Mnt fac',
          dataIndex: 'MNT_TTC',
          key: 'MNT_TTC',
          width: 100,
          render: text => text ? formatAmount(text) : '-',
        }
      ]
    },
    {
      title: () => (
        <div style={{ color: '#faad14', fontWeight: 600 }}>Remarque</div>
      ),
      dataIndex: 'remarque',
      key: 'remarque',
      width: 200,
      render: text => text || '-',
    },
    
    {
      title: () => (
        <div style={{ color: '#52c41a', fontWeight: 600 }}>Expédition</div>
      ),
      children: [
        {
          title: 'Date exp',
          dataIndex: 'date_exp',
          key: 'date_exp',
          width: 100,
          editable: true,
          render: text => text || '-',
        },
        {
          title: 'Réf exp',
          dataIndex: 'ref_exp',
          key: 'ref_exp',
          width: 120,
          editable: true,
        },
        {
          title: 'nbr Colis',
          dataIndex: 'nbr_colis',
          key: 'nbr_colis',
          width: 80,
          editable: true,
        },
       
      ]
    },
    {
      title: () => (
        <div style={{ color: '#f5222d', fontWeight: 600 }}>Livraison & Paiement</div>
      ),
      children: [
        {
          title: 'Ville Dest',
          dataIndex: 'ville_dest',
          key: 'ville_dest',
          width: 110,
          editable: true,
        },
        {
          title: 'mt_remb',
          dataIndex: 'mt_remb',
          key: 'mt_remb',
          width: 130,
          editable: true,
          render: text => text ? formatAmount(text) : '-',
        },
        {
          title: 'M. Livr.',
          dataIndex: 'm_liv',
          key: 'm_liv',
          width: 100,
          editable: true,
        },
        {
          title: 'M. Paie.',
          dataIndex: 'm_pai',
          key: 'm_pai',
          width: 100,
          editable: true,
        },
       
        
        {
          title: 'Port',
          dataIndex: 'port_p',
          key: 'port_p',
          width: 80,
          editable: true,
        },
        
      ]
    },
    
    {
      title: () => (
        <div style={{ color: '#13c2c2', fontWeight: 600 }}>Observ.</div>
      ),
      dataIndex: 'obser',
      key: 'obser',
      width: 150,
      editable: true,
      render: text => text || '-',
    },
  ];

  // Get all editable column keys
  const getEditableColumnKeys = () => {
    const editableFields = ['saisie_info_recu', 'date_exp', 'ref_exp', 'nbr_colis', 'port_p', 'mt_remb', 'm_pai', 'm_liv', 'obser', 'ville_dest'];
    return columns
      .filter(col => {
        if (col.dataIndex && editableFields.includes(col.dataIndex)) {
          return true;
        }
        if (col.children) {
          return col.children.some(child => editableFields.includes(child.dataIndex));
        }
        return false;
      })
      .flatMap(col => {
        if (col.dataIndex) {
          return [col.dataIndex];
        }
        if (col.children) {
          return col.children
            .filter(child => editableFields.includes(child.dataIndex))
            .map(child => child.dataIndex);
        }
        return [];
      });
  };

  // Function to navigate between cells with wrap-around
  const navigateToCell = (rowIndex, colIndex) => {
    const currentData = getCurrentTabData();
    const editableColumns = getEditableColumnKeys();
    
    // Ensure we have items
    if (!Array.isArray(currentData) || currentData.length === 0) {
      return;
    }
    
    // Handle wrap-around for columns
    if (colIndex < 0) {
      colIndex = editableColumns.length - 1;
      rowIndex = rowIndex - 1;
    } else if (colIndex >= editableColumns.length) {
      colIndex = 0;
      rowIndex = rowIndex + 1;
    }
    
    // Handle wrap-around for rows
    if (rowIndex < 0) {
      rowIndex = currentData.length - 1;
    } else if (rowIndex >= currentData.length) {
      rowIndex = 0;
    }
    
    const targetRow = currentData[rowIndex];
    if (!targetRow || !targetRow.id) {
      return;
    }
    
    const targetColumn = editableColumns[colIndex];
    
    // Close any open edit cells first (find any cell in edit mode and simulate ESC press)
    const activeEditCells = document.querySelectorAll('.ant-form-item');
    if (activeEditCells.length > 0) {
      // There's an active edit cell, let's force close it first
      const escEvent = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true });
      activeEditCells[0].dispatchEvent(escEvent);
      
      // Give a small delay before activating the new cell
      setTimeout(() => {
        // Now set the active cell and row
        setActiveCell({ rowKey: targetRow.id, columnKey: targetColumn });
        setActiveRowKey(targetRow.id);
        
        // Try to find and click the cell to enter edit mode
        setTimeout(() => {
          const cellSelector = `[data-cell-id="${targetRow.id}-${targetColumn}"]`;
          const domCell = document.querySelector(cellSelector);
          
          if (domCell) {
            domCell.click();
          }
        }, 50);
      }, 100);
    } else {
      // No active edit cells, we can directly activate the new cell
      setActiveCell({ rowKey: targetRow.id, columnKey: targetColumn });
      setActiveRowKey(targetRow.id);
      
      // Try to find and click the cell to enter edit mode
      setTimeout(() => {
        const cellSelector = `[data-cell-id="${targetRow.id}-${targetColumn}"]`;
        const domCell = document.querySelector(cellSelector);
        
        if (domCell) {
          domCell.click();
        }
      }, 50);
    }
  };

  // Get current tab data
  const getCurrentTabData = () => {
    switch (activeTab) {
      case '1':
        return filteredWithoutExp;
      case '2':
        return filteredWithExp;
      default:
        return [];
    }
  };

  // Styling
  const tableStyle = {
    background: '#ffffff',
    borderRadius: sizeStep.lg,
    boxShadow: `0 ${sizeStep.sm}px ${sizeStep.lg}px rgba(0, 0, 0, 0.05)`,
  };

  const filterCardStyle = {
    marginBottom: sizeStep.lg,
    backgroundColor: "#f9fafc",
    borderRadius: sizeStep.md,
    border: "1px solid #eaedf2",
    overflow: "hidden"
  };

  // Render the action bar with save/discard buttons
  const renderActionBar = () => {
    const changesCount = pendingChanges.size;
    
    return (
      <div style={{ marginBottom: sizeStep.lg, padding: sizeStep.lg, background: '#f7fafc', borderRadius: sizeStep.md, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {changesCount > 0 ? (
            <Badge count={changesCount} offset={[5, 0]}>
              <span style={{ marginRight: sizeStep.lg, fontWeight: 500 }}>Modifications en attente</span>
            </Badge>
          ) : (
            <span style={{ color: '#8c8c8c' }}>Aucune modification en attente</span>
          )}
        </div>
        
        <Space>
          <PermissionButton
            danger
            icon={<ClearOutlined />}
            onClick={discardAllChanges}
            disabled={changesCount === 0}
          >
            Annuler
          </PermissionButton>
          
          <Popconfirm
            title="Enregistrer toutes les modifications"
            description={`Êtes-vous sûr de vouloir enregistrer ${changesCount} modifications?`}
            onConfirm={saveAllChanges}
            okText="Oui"
            cancelText="Non"
            disabled={changesCount === 0}
          >
            <PermissionButton permissionModule="expedition" permissionAction="update"
              type="primary"
              icon={savingChanges ? <SyncOutlined spin /> : <SaveOutlined />}
              disabled={changesCount === 0 || savingChanges}
            >
              Enregistrer modif.
            </PermissionButton>
          </Popconfirm>
        </Space>
      </div>
    );
  };

  // Filter section
  const renderFilterSection = () => {
    return (
      <Collapse 
        defaultActiveKey={[]} 
        style={filterCardStyle}
        expandIcon={({ isActive }) => (
          <DownOutlined rotate={isActive ? 180 : 0} style={{ color: '#1890ff' }} />
        )}
      >
        <Panel 
          header={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <FilterOutlined style={{ marginRight: sizeStep.sm, color: '#1890ff' }} />
              Filtres d'Expédition
            </span>
          } 
          key="1"
        >
          <Form
            form={filterForm}
            layout="horizontal"
            onValuesChange={handleFilterChange}
          >
            {/* First row: expedition number, devis, search button */}
            <Row gutter={[sizeStep.lg, sizeStep.md]} align="middle">
              <Col xs={24} sm={8}>
                <Form.Item name="ref_exp" label="Réf. Expédition" style={{ marginBottom: sizeStep.md }}>
                  <Input 
                    placeholder="Filtrer par réf. exp"
                    prefix={<TruckOutlined style={{ color: '#bfbfbf' }} />}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="ref_dev" label="Réf. Devis" style={{ marginBottom: sizeStep.md }}>
                  <Input 
                    placeholder="Filtrer par réf. devis"
                    prefix={<ShoppingOutlined style={{ color: '#bfbfbf' }} />}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />}
                  style={{ marginBottom: sizeStep.md }}
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
            
            {/* Second row: date, facture, reset button */}
            <Row gutter={[sizeStep.lg, sizeStep.md]} align="middle">
              <Col xs={24} sm={8}>
                <Form.Item name="date_exp" label="Date Expédition" style={{ marginBottom: sizeStep.md }}>
                  <RangePicker 
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder={['Date début', 'Date fin']}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="ref_fac" label="Réf. Facture" style={{ marginBottom: sizeStep.md }}>
                  <Input 
                    placeholder="Filtrer par réf. facture"
                    prefix={<FileTextOutlined style={{ color: '#bfbfbf' }} />}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
                <Button 
                  icon={<ClearOutlined />} 
                  onClick={resetFilters}
                  style={{ marginBottom: sizeStep.md }}
                >
                  Réinitialiser
                </Button>
              </Col>
            </Row>
          </Form>
        </Panel>
      </Collapse>
    );
  };

  // Components for editable table
  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  // Prepare columns for editing
  const getEditableColumns = () => {
    return columns.map(col => {
      if (col.children) {
        return {
          ...col,
          children: col.children.map((childCol) => {
            if (!childCol.editable) {
              return childCol;
            }
            return {
              ...childCol,
              onCell: (record, rowIndex) => {
                if (!record) return {};
                
                // Get the actual column index among editable columns
                const editableColIndex = getEditableColumnKeys().indexOf(childCol.dataIndex);
                
                return {
                  record,
                  editable: childCol.editable,
                  dataIndex: childCol.dataIndex,
                  title: childCol.title,
                  handleChange: (value) => {
                    // This won't be used directly, but we keep it for compatibility
                  },
                  isModified: pendingChanges.has(record.id) && pendingChanges.get(record.id)[childCol.dataIndex] !== undefined,
                  inputType: childCol.dataIndex === 'date_exp' ? 'date' : 'text',
                  rowIndex: rowIndex,
                  colIndex: editableColIndex,
                  navigateToCell: navigateToCell
                };
              }
            };
          })
        };
      }
      
      if (!col.editable) {
        return col;
      }
      
      return {
        ...col,
        onCell: (record, rowIndex) => {
          if (!record) return {};
          
          // Get the actual column index among editable columns
          const editableColIndex = getEditableColumnKeys().indexOf(col.dataIndex);
          
          return {
            record,
            editable: col.editable,
            dataIndex: col.dataIndex,
            title: col.title,
            handleChange: (value) => {
              // This won't be used directly, but we keep it for compatibility
            },
            isModified: pendingChanges.has(record.id) && pendingChanges.get(record.id)[col.dataIndex] !== undefined,
            inputType: col.dataIndex === 'date_exp' ? 'date' : 'text',
            rowIndex: rowIndex,
            colIndex: editableColIndex,
            navigateToCell: navigateToCell
          };
        }
      };
    });
  };

  // Get a row class name based on its state (modified and/or active)
  const getRowClassName = (record) => {
    if (!record || !record.id) return '';
    
    const classes = [];
    
    if (pendingChanges.has(record.id)) {
      classes.push('modified-row');
    }
    
    if (activeRowKey === record.id) {
      classes.push('active-row');
    }
    
    return classes.join(' ');
  };

  // Main render function
  const renderTable = (data, loading, error) => {
    if (error) {
      return (
        <div style={{ textAlign: 'center', padding: '20px', color: '#f5222d' }}>
          <InfoCircleOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
          <p>Erreur de chargement: {error}</p>
        </div>
      );
    }
    
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: '#5a6a85' }}>Chargement des données...</p>
        </div>
      );
    }
    
    return (
      <KeyboardNavContext.Provider value={{ 
        activeCell, 
        setActiveCell, 
        navigateToCell,
        activeRowKey,
        setActiveRowKey,
        pendingChanges,
        setPendingChanges
      }}>
        <Table
          components={components}
          rowClassName={getRowClassName}
          columns={getEditableColumns()}
          dataSource={data}
          rowKey="id"
          style={tableStyle}
          scroll={{ x: 1800 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} entrées`
          }}
          size="small"
          bordered
          title={() => (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={5} style={{ margin: 0 }}>
                {activeTab === '1' ? 'Expéditions Manquantes' : 'Avec Expédition'}
              </Title>
             
            </div>
          )}
        />
      </KeyboardNavContext.Provider>
    );
  };

  return (
    <div>
      {/* Filter Section */}
      {renderFilterSection()}
      
      {/* Action Bar (for save/discard) */}
      {renderActionBar()}
      
      {/* Tabs */}
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane 
          tab={
            <span>
              <Tag color="red" style={{ marginRight: '8px' }}>!</Tag>
              Expédition Manquante
            </span>
          } 
          key="1"
        >
          {renderTable(filteredWithoutExp, loadingWithoutExp, errorWithoutExp)}
        </TabPane>
        
        <TabPane 
          tab={
            <span>
              <Tag color="green" style={{ marginRight: '8px' }}>✓</Tag>
              Avec Expédition
            </span>
          } 
          key="2"
        >
          {renderTable(filteredWithExp, loadingWithExp, errorWithExp)}
        </TabPane>
      </Tabs>
      
      {/* Template Selection Modal */}
      <TemplateSelectionModal
        visible={templateModalVisible}
        onCancel={() => setTemplateModalVisible(false)}
        onTemplateSelect={handleTemplateSelect}
        rowData={selectedRowData}
      />
      
      {/* Display help text for keyboard shortcuts */}
      <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
        <p>Utilisez les flèches du clavier pour naviguer entre les cellules. Appuyez sur Escape pour annuler, Tab pour aller à la cellule suivante, et Enter pour valider et passer à la ligne suivante. Les valeurs modifiées apparaissent en bleu et sont enregistrées uniquement lorsque vous cliquez sur "Enregistrer les modifications".</p>
      </div>
      
      <style jsx global>{`
  .ant-table-thead > tr > th {
    background-color: #f7f9fc;
    font-weight: 600;
    color: #1a3353;
    padding: 4px 6px !important;
  }
  
  /* Add styles for the colored group headers */
  .ant-table-thead > tr:first-child > th {
    background-color: #fafafa;
    border-bottom: 2px solid #f0f0f0;
  }
  
  /* Rest of the styles remain unchanged */
  .ant-table-tbody > tr:hover > td {
    background-color: #f0f7ff;
  }
  .ant-tag {
    margin-right: 0;
    font-size: 12px;
  }
  .ant-tabs-tab {
    padding: 8px 16px;
  }
  .ant-table-row {
    cursor: pointer;
  }
  
  /* EditableCell styling */
  .editable-cell-value-wrap {
    padding: 0 2px;
    line-height: 1.1;
    min-height: 24px;
    display: flex;
    align-items: center;
  }
  .compact-edit-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 2px;
    padding: 0;
  }
  .compact-input {
    font-size: 14px !important;
    padding: 0 2px !important;
    height: 25px !important;
  }
  
  /* Styling for modified rows */
  .modified-row {
    background-color: #e6f7ff !important;
  }
  .modified-row:hover > td {
    background-color: #bae7ff !important;
  }
  
  /* Active row styling with horizontal black lines */
  .active-row td {
    outline: none !important;
    border-top: 2px solid black !important;
    border-bottom: 2px solid black !important;
  }
  
  .active-row td:first-child {
    border-left: none !important;
  }
  
  .active-row td:last-child {
    border-right: none !important;
  }
  
  /* Table cell padding */
  .ant-table-cell {
    padding: 4px 6px !important;
    line-height: 1.1 !important;
  }
`}</style>
    </div>
  );
};

export default JournalExpTable;